import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import ExpertiseCards from "../components/ExpertiseCards";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import Lottie from "react-lottie-player";
import animationData from "../waving.json";
import ClientReviews from "../components/ClientReviews";
import { FaPen, FaSpinner } from "react-icons/fa";

const About = ({ role }) => {
  const [isVisible, setIsVisible] = useState(false);
  const heroRef = useRef(null);
  const bioRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.3]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.9]);

  // ====================== STATES FOR HERO SECTION (Editable) ======================
  const [heroHeading, setHeroHeading] = useState("Meet Brian Karsh:");
  const [heroSubheading, setHeroSubheading] = useState(
    "Your Multicultural Real Estate Expert in Lafayette, Moraga, Orinda, and Walnut Creek"
  );
  const [heroBackgroundImage, setHeroBackgroundImage] = useState("/images/About.jpg");

  const [isUploading, setIsUploading] = useState(false);
const [uploadProgress, setUploadProgress] = useState(0);


const [heroBackgroundImages, setHeroBackgroundImages] = useState(["/images/About.jpg"]);
const [editHeroBackgroundImages, setEditHeroBackgroundImages] = useState([]);

  // ====================== EDIT STATES FOR HERO SECTION ======================
  const [editHeroHeading, setEditHeroHeading] = useState("");
  const [editHeroSubheading, setEditHeroSubheading] = useState("");
  const [editHeroBackgroundImage, setEditHeroBackgroundImage] = useState("");
  const [showHeroEditForm, setShowHeroEditForm] = useState(false);

  // ====================== STATES FOR BIO SECTION (Editable) ======================
  const [heading, setHeading] = useState("I'm Brian Karsh");
  const [subheading, setSubheading] = useState("Your Las Vegas Realtor");
  const [description, setDescription] = useState(
    "In the dynamic realms of California's real estate market, particularly in Lafayette, Moraga, Orinda, and Walnut Creek, Brian Karsh stands out not just as a realtor but as a local expert..."
  );
  const [titles, setTitles] = useState([
    "A Remarkable Real Estate Journey in Las Vegas",
    "Empowering Clients with Knowledge in Las Vegas Real Estate",
    "From Global Experiences to Local Mastery in Lafayette, Moraga, and Orinda",
    "Personalized Service, Every Step of the Way in Las Vegas",
    "Testimonial - A Client's Success Story in Las Vegas",
    "More Than an Agent, a Trusted Partner in Las Vegas Real Estate",
  ]);

  // ====================== EDIT STATES & MODAL VISIBILITY FOR BIO ======================
  const [editHeading, setEditHeading] = useState("");
  const [editSubheading, setEditSubheading] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editTitles, setEditTitles] = useState([]);
  const [showEditForm, setShowEditForm] = useState(false);

  const backendUrl = 'https://python-backend-68898252993.us-central1.run.app';


  // ====================== FETCH EXISTING DATA ON MOUNT ======================
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Hero Section Data
        const heroResponse = await fetch(`${backendUrl}/api/get_hero_section`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const heroData = await heroResponse.json();
        if (heroResponse.ok) {
          if (heroData.heading) setHeroHeading(heroData.heading);
          if (heroData.subheading) setHeroSubheading(heroData.subheading);
          if (heroData.background_images && Array.isArray(heroData.background_images)) {
            setHeroBackgroundImages(heroData.background_images);
          } else if (heroData.background_image) {
            setHeroBackgroundImages([heroData.background_image]);
          }
                }

        // Fetch Bio Section Data
        const bioResponse = await fetch(`${backendUrl}/api/get_bio_section`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        const bioData = await bioResponse.json();
        if (bioResponse.ok) {
          if (bioData.heading) setHeading(bioData.heading);
          if (bioData.subheading) setSubheading(bioData.subheading);
          if (bioData.description) setDescription(bioData.description);
          if (Array.isArray(bioData.titles)) setTitles(bioData.titles);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  // ====================== HANDLERS FOR HERO EDIT ======================
  const handleHeroPenClick = () => {
    setEditHeroHeading(heroHeading);
    setEditHeroSubheading(heroSubheading);
    setEditHeroBackgroundImages([...heroBackgroundImages]);
    setShowHeroEditForm(true);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
  
    const fileArray = Array.from(files);
    setIsUploading(true);
    setUploadProgress(0);
  
    const uploadedUrls = [];
  
    for (let [index, file] of fileArray.entries()) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "Brian_Karsh");
  
      try {
        const response = await fetch("https://api.cloudinary.com/v1_1/dyglrkcfq/image/upload", {
          method: "POST",
          body: formData,
        });
  
        const result = await response.json();
        if (response.ok) {
          uploadedUrls.push(result.secure_url);
          setUploadProgress(Math.round(((index + 1) / fileArray.length) * 100));
        } else {
          console.error("Error uploading to Cloudinary:", result.message);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  
    setEditHeroBackgroundImages(uploadedUrls);
    setIsUploading(false);
    setUploadProgress(100);
  };

  // Replace the existing handleFileChange function with this one:


  const handleHeroSave = async (e) => {
    e.preventDefault();
    const dataToSave = {
      heading: editHeroHeading,
      subheading: editHeroSubheading,
      background_images: editHeroBackgroundImages,
    };
  
    try {
      const response = await fetch(`${backendUrl}/api/save_hero_section`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(dataToSave),
      });
      const resData = await response.json();
  
      if (response.ok) {
        setHeroHeading(dataToSave.heading);
        setHeroSubheading(dataToSave.subheading);
        setHeroBackgroundImages(dataToSave.background_images);
        setShowHeroEditForm(false);
      } else {
        console.error("Failed to save hero data:", resData.message);
      }
    } catch (error) {
      console.error("Error saving hero data:", error);
    }
  };

  // ====================== HANDLERS FOR BIO EDIT ======================
  const handlePenClick = () => {
    setEditHeading(heading);
    setEditSubheading(subheading);
    setEditDescription(description);
    setEditTitles([...titles]);
    setShowEditForm(true);
  };

  const handleTitleChange = (index, newVal) => {
    const updated = [...editTitles];
    updated[index] = newVal;
    setEditTitles(updated);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const dataToSave = {
      heading: editHeading || "",
      subheading: editSubheading || "",
      description: editDescription || "",
      titles: editTitles,
    };

    try {
      const response = await fetch(`${backendUrl}/api/save_bio_section`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(dataToSave),
      });
      const resData = await response.json();

      if (response.ok) {
        setHeading(dataToSave.heading);
        setSubheading(dataToSave.subheading);
        setDescription(dataToSave.description);
        setTitles(dataToSave.titles);
        setShowEditForm(false);
      } else {
        console.error("Failed to save bio data:", resData.message);
      }
    } catch (error) {
      console.error("Error saving bio data:", error);
    }
  };

  // ====================== USEEFFECT FOR INTERSECTION OBSERVER ======================
  useEffect(() => {
    setIsVisible(true);

    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll(".animate-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // ====================== BULLET POINT ICONS ======================
  const bulletIcons = [
    <svg
      key="icon-1"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>,
    <svg
      key="icon-2"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>,
    <svg
      key="icon-3"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>,
    <svg
      key="icon-4"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11"
      />
    </svg>,
    <svg
      key="icon-5"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
    </svg>,
    <svg
      key="icon-6"
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>,
  ];

  const bulletItems = titles.map((t, i) => ({
    icon: bulletIcons[i] || <></>,
    title: t,
  }));

  return (
    <div className="overflow-x-hidden">
      {/* ====================== HERO SECTION ====================== */}
     {/* ====================== HERO SECTION WITH SLIDESHOW ====================== */}
<motion.div
  ref={heroRef}
  className="relative w-screen h-screen"
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  transition={{ duration: 1 }}
>
  {/* Swiper Slideshow */}
  <Swiper
    modules={[Pagination, Autoplay]}
    pagination={{ clickable: true }}
    autoplay={{ delay: 5000, disableOnInteraction: false }}
    loop={heroBackgroundImages.length > 1}
    className="w-full h-full"
  >
    {heroBackgroundImages.map((image, index) => (
      <SwiperSlide
      key={index}
      style={{
        backgroundImage: `url('${image}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
      className="relative"
    >
      {/* Overlay */}
      <motion.div
        className="absolute inset-0 bg-black/40"
        style={{ opacity }}
      ></motion.div>
      
      {/* Content */}
      <motion.div
        className="absolute inset-0 flex flex-col justify-center items-start px-8 md:px-16 text-white z-10"
        style={{ scale }}
      >
        <motion.h1
          className="text-4xl md:text-5xl font-bold mb-4 home-heading"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {heroHeading}
        </motion.h1>
        <motion.p
          className="text-lg md:text-xl mb-6 leading-relaxed max-w-2xl"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          {heroSubheading}
        </motion.p>
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <motion.button
            className="bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-md flex items-center gap-2"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              const bioSection = document.querySelector("#bio-section");
              bioSection.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Learn More
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.button>
        </motion.div>
      </motion.div>
    </SwiperSlide>
    ))}
  </Swiper>

  {/* Hero Edit Button */}
{/* Hero Edit Button */}
{role === "owner" && (
  <motion.button
    onClick={handleHeroPenClick}
    className="absolute bottom-4 left-4 z-50 text-white hover:text-gray-300 bg-black/30 hover:bg-black/50 p-2 rounded-full backdrop-blur-sm"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
  >
    <FaPen size={25} />
  </motion.button>
)}

</motion.div>

      {/* ====================== HERO EDIT FORM (MODAL) ====================== */}
      {showHeroEditForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-3xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold dark:text-white">Edit Hero Section</h2>
              <button
                onClick={() => setShowHeroEditForm(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleHeroSave} className="space-y-4">
            <div>
  <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
    Background Images
  </label>
  <div className="space-y-2">
    <input
      type="file"
      multiple
      accept="image/*"
      onChange={handleImageUpload}
      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2"
      disabled={isUploading}
    />
    
    {/* Loading Indicator */}
    {isUploading && (
      <div className="flex items-center justify-center p-4">
        <FaSpinner className="animate-spin h-6 w-6 text-blue-500 mr-2" />
        <span className="text-gray-700">Uploading... {uploadProgress}%</span>
      </div>
    )}
    
    {/* Image Previews */}
    {editHeroBackgroundImages.length > 0 && (
      <div className="grid grid-cols-3 gap-2 mt-2">
        {editHeroBackgroundImages.map((image, index) => (
          <div key={index} className="relative">
            <img
              src={image}
              alt={`Preview ${index + 1}`}
              className="w-full h-24 object-cover rounded-md"
            />
            <button
              type="button"
              onClick={() => {
                const newImages = [...editHeroBackgroundImages];
                newImages.splice(index, 1);
                setEditHeroBackgroundImages(newImages);
              }}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
</div>
              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Heading
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2"
                  value={editHeroHeading}
                  onChange={(e) => setEditHeroHeading(e.target.value)}
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subheading
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2"
                  value={editHeroSubheading}
                  onChange={(e) => setEditHeroSubheading(e.target.value)}
                />
              </div>

           
            </form>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowHeroEditForm(false)}
                className="px-4 py-2 rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors duration-150 focus:outline-none dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleHeroSave}
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-150 focus:outline-none"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <hr className="animate-pulse" />
      <br />
      <br />
      <br />

      {/* ====================== ABOUT CONTENT SECTION ====================== */}
      <div id="bio-section" className="container mx-auto py-16 px-8 md:px-16 relative">
        {/* If user is owner, show the edit (pen) icon */}
        {role === "owner" && (
          <button
            onClick={handlePenClick}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <FaPen size={18} />
          </button>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">

          {/* Left Column: Lottie Animation */}
          <motion.div
             className="order-1 md:order-none w-full flex items-center justify-center animate-on-scroll"

            ref={bioRef}
            initial={{ opacity: 0, y: 100 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              whileHover={{
                scale: 1.05,
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)",
              }}
              transition={{ type: "spring", stiffness: 300 }}
              className="w-full"
            >



              <div className="flex justify-center">
  {/* Responsive (mobile) Lottie - smaller */}
  <div className="block md:hidden">
    <Lottie
      animationData={animationData}
      play
      className="w-[160px]" // Small for mobile
    />
  </div>

  {/* Desktop Lottie - larger */}
  <div className="hidden md:block">
    <Lottie
      animationData={animationData}
      play
      style={{ transform: "scale(2.3)" }}
    />
  </div>
</div>

{isUploading && (
  <div className="flex items-center justify-center p-4">
    <FaSpinner className="animate-spin h-6 w-6 text-blue-500 mr-2" />
    <span className="text-gray-700">Uploading... {uploadProgress}%</span>
  </div>
)}


            </motion.div>
          </motion.div>

          {/* Right Column: Biography */}
          <motion.div
          className="order-2 md:order-none w-full flex flex-col justify-center animate-on-scroll"

            initial={{ opacity: 0, x: 100 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Editable heading/subheading/description */}
            <motion.h2
              className="text-3xl font-bold text-red-600 mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {heading}
            </motion.h2>
            <motion.h3
              className="text-4xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              {subheading}
            </motion.h3>

            <motion.p
              className="text-gray-700 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              {description}
            </motion.p>

            {/* Expertise Points with staggered animations */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.9 }}
            >
              {bulletItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-start"
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.9 + index * 0.1 }}
                >
                  <motion.div
                    className="text-red-600 mr-4"
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    {item.icon}
                  </motion.div>
                  <div>
                    <h4 className="font-bold text-lg">{item.title}</h4>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.5 }}
            >
              <p className="text-gray-700 mb-6">
                Reach out to Brian today to start your real estate journey in
                Lafayette, Moraga, and Orinda with confidence.
              </p>
              <motion.a
                onClick={() => {
                  window.open(
                    "https://mail.google.com/mail/?view=cm&fs=1&to=BK@BrianKarsh.com",
                    "_blank"
                  );
                }}
                href="#contact"
                className="bg-red-600 text-white py-3 px-8 rounded-md inline-flex items-center"
                whileHover={{ scale: 1.05, backgroundColor: "#9b2c2c" }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                Get to know Brian
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 ml-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <path
                    fillRule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </motion.svg>
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ====================== BIO EDIT FORM (MODAL) ====================== */}
      {showEditForm && (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
    {/* Increased max-w to 3xl and added min-h so it's bigger horizontally */}
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-3xl w-full relative min-h-[400px]">
      {/* Modal header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold dark:text-white">Edit About Section</h2>
        <button
          onClick={() => setShowEditForm(false)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Two-column layout for the form fields */}
      <form onSubmit={handleSave} className="flex flex-col md:flex-row gap-6">
        {/* Left Column (Heading, Subheading, Description) */}
        <div className="flex-1 space-y-4 flex flex-col items-center text-center md:items-start md:text-left">
  {/* Heading */}
  <div className="w-full md:w-auto">
    <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
      Heading
    </label>
    <input
      type="text"
      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2"
      value={editHeading}
      onChange={(e) => setEditHeading(e.target.value)}
    />
  </div>

  {/* Subheading */}
  <div className="w-full md:w-auto">
    <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
      Subheading
    </label>
    <input
      type="text"
      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2"
      value={editSubheading}
      onChange={(e) => setEditSubheading(e.target.value)}
    />
  </div>

  {/* Description */}
  <div className="w-full">
    <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
      Description
    </label>
    <textarea
      rows={6}
      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 resize-none"
      value={editDescription}
      onChange={(e) => setEditDescription(e.target.value)}
    />
  </div>





</div>

        {/* Right Column (Bullet Points) */}
        <div className="flex-1 space-y-4">
          <label className="block font-medium text-gray-700 dark:text-gray-300 mb-1">
            Bullet Points
          </label>
          <div className="flex flex-col gap-2">
            {editTitles.map((bullet, idx) => (
              <input
                key={idx}
                type="text"
                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2"
                value={bullet}
                onChange={(e) => handleTitleChange(idx, e.target.value)}
                placeholder={`Bullet ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </form>

      {/* Modal Footer with buttons (place at bottom) */}
      <div className="mt-6 flex justify-end space-x-3 border-t pt-4 border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setShowEditForm(false)}
          className="px-4 py-2 rounded-md text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors duration-150 focus:outline-none dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSave}
          className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-150 focus:outline-none"
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
)}


      {/* Expertise cards component */}
      <AnimatedExpertiseCards role={role} />
      <ClientReviews />
      {/* <ExpertiseCards role={role} /> */}
    </div>
  );
};

// ====================== Enhanced ExpertiseCards component with animations ======================
const AnimatedExpertiseCards = ({ role }) => {
  const [visibleCards, setVisibleCards] = useState([]);
  const cardsRef = useRef(null);

  useEffect(() => {
    const options = {
      threshold: 0.1,
      rootMargin: "0px 0px -100px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        startCardAnimation();
        observer.unobserve(cardsRef.current);
      }
    }, options);

    if (cardsRef.current) {
      observer.observe(cardsRef.current);
    }

    return () => {
      if (cardsRef.current) {
        observer.unobserve(cardsRef.current);
      }
    };
  }, []);

  const startCardAnimation = () => {
    // Animate cards one by one with a delay
    const interval = setInterval(() => {
      setVisibleCards((prev) => {
        if (prev.length >= 6) {
          clearInterval(interval);
          return prev;
        }
        return [...prev, prev.length];
      });
    }, 200);
  };

  return (
    <div ref={cardsRef} className="bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <motion.h2
          className="text-3xl font-bold text-center mb-12 text-gray-800"
          initial={{ opacity: 0, y: -50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          Brian's Areas of Expertise
        </motion.h2>
        {/* Removed the grid container, it's now in the ExpertiseCards component */}
        <ExpertiseCards visibleCards={visibleCards} role={role} />
      </div>
    </div>
  );
};


export default About;















